/**
 * Public utility functions for formatting
 */

// =============================================================================
// Formatting (number -> string)
// =============================================================================

/**
 * Format USDC amount for display
 * @example formatUsdc(100.50) => "$100.50"
 * @example formatUsdc(100.50, { symbol: false }) => "100.50"
 */
export function formatUsdc(
	amount: number,
	options?: { symbol?: boolean; decimals?: number },
): string {
	const { symbol = true, decimals = 2 } = options ?? {}
	const formatted = amount.toFixed(decimals)
	return symbol ? `$${formatted}` : formatted
}

/**
 * Format share amount for display
 * @example formatShares(100.5) => "100.5"
 * @example formatShares(1000) => "1,000"
 */
export function formatShares(amount: number, options?: { decimals?: number }): string {
	const { decimals = 2 } = options ?? {}
	// Remove trailing zeros
	const fixed = amount.toFixed(decimals)
	const trimmed = parseFloat(fixed).toString()
	// Add thousand separators
	const [int, dec] = trimmed.split(".")
	const intFormatted = (int ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
	return dec ? `${intFormatted}.${dec}` : intFormatted
}

/**
 * Format price as percentage
 * @example formatPrice(0.45) => "45%"
 * @example formatPrice(0.456, { decimals: 1 }) => "45.6%"
 */
export function formatPrice(price: number, options?: { decimals?: number }): string {
	const { decimals = 0 } = options ?? {}
	return `${(price * 100).toFixed(decimals)}%`
}

/**
 * Format basis points as percentage
 * @example formatBps(150) => "1.5%"
 */
export function formatBps(bps: number): string {
	return `${(bps / 100).toFixed(2)}%`
}

// =============================================================================
// Address Formatting
// =============================================================================

/**
 * Truncate address for display
 * @example truncateAddress("0x1234567890abcdef1234567890abcdef12345678") => "0x1234...5678"
 */
export function truncateAddress(address: string, chars = 4): string {
	if (address.length <= chars * 2 + 2) {
		return address
	}
	return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}
